import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import "@fortawesome/fontawesome-free/css/all.css";
import { useEffect, useState, lazy, Suspense, useMemo, type JSX } from "react";
import { ApolloClient, type InMemoryCache } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { persistCache, LocalStorageWrapper } from "apollo3-cache-persist";
import * as Sentry from "@sentry/react";
import AuthenticatedLayout from "./ui/authenticated-layout";
import { Box, CircularProgress } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DataFetcher } from "@/ui/data-fetcher";
import { LOCALE } from "@/dayjs";
import { link } from "@/apollo-link";
import { createCache } from "@/apollo-cache";

const Home = lazy(() => import("./map/assets-map/assets-map-page"));
const NotesPage = lazy(() => import("./notes/notes-page"));
const AllNotesPage = lazy(() => import("./notes/all-notes-page"));
const MetricsPage = lazy(() => import("./metrics/metrics-page"));
const PropertiesPage = lazy(() => import("./properties/properties-page"));
const ManageAccessPage = lazy(() => import("./users/manage-access-page"));
const InspectionsPage = lazy(() => import("./inspections/inspections-page"));
const ManageAssets = lazy(() => import("./map/edit-map/edit-assets-page"));
const AssetsTable = lazy(() => import("./assets-table/assets-table-page"));
const Login = lazy(() => import("./auth/login-page"));
const BatchEntryPage = lazy(() => import("./batch-entry/batch-entry-page"));
const InspectionTimelinePage = lazy(
  () => import("./inspection-frequency/inspection-timeline-page"),
);
const DefaultPropertiesPage = lazy(
  () => import("./properties/default-properties/default-properties-page"),
);
const LoadsPage = lazy(() => import("./loads/loads-page"));
const CustomFieldsPage = lazy(
  () => import("./custom-fields/custom-fields-page"),
);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0,
  enabled: import.meta.env.PROD,
  environment: import.meta.env.MODE,
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const fallback = (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <CircularProgress />
  </Box>
);

export default function App(): JSX.Element {
  const [cache, setCache] = useState<InMemoryCache | null>(null);
  useEffect(() => {
    void (async () => {
      const cache = createCache();
      await persistCache({
        cache,
        storage: new LocalStorageWrapper(window.localStorage),
        maxSize: false,
      });
      setCache(cache);
    })();
  }, []);

  const apolloClient = useMemo(() => {
    if (cache == null) return null;

    return new ApolloClient({
      cache,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
      link,
    });
  }, [cache]);

  if (!apolloClient) return fallback;

  const router = sentryCreateBrowserRouter([
    {
      element: <AuthenticatedLayout />,
      children: [
        { path: "/", element: <Navigate to="/home" /> },
        { path: "/home", element: <Home /> },
        { path: "/manage-assets", element: <ManageAssets /> },
        { path: "/assets-table", element: <AssetsTable /> },
        { path: "/all-notes", element: <AllNotesPage /> },
        { path: "/metrics", element: <MetricsPage /> },
        {
          path: "/configuration/inspection-timeline",
          element: <InspectionTimelinePage />,
        },
        {
          path: "/configuration/default-properties",
          element: <DefaultPropertiesPage />,
        },
        { path: "/configuration/loads", element: <LoadsPage /> },
        { path: "/configuration/custom-fields", element: <CustomFieldsPage /> },
        { path: "/batch-entry", element: <BatchEntryPage /> },
        {
          path: "/home/inspections",
          element: <InspectionsPage />,
          handle: { backButton: true },
        },
        {
          path: "/home/notes",
          element: <NotesPage />,
          handle: { backButton: true },
        },
        {
          path: "/home/properties",
          element: <PropertiesPage />,
          handle: { backButton: true },
        },
        { path: "/manage-access", element: <ManageAccessPage /> },
      ],
    },
    { path: "/login", element: <Login /> },
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={LOCALE}>
      <ApolloProvider client={apolloClient}>
        <Suspense fallback={fallback}>
          <DataFetcher />
          <RouterProvider router={router} />
        </Suspense>
      </ApolloProvider>
    </LocalizationProvider>
  );
}
